import { CartItem } from "../store/slices/cartSlice.ts";
import { ProfileResponse } from "../types";

export const WHOLESALE_INITIAL_ORDER_MINIMUM = 24;
export const WHOLESALE_REORDER_MINIMUM = 12;

/*
 * Mirrors the server-side minimums enforced in
 * wholesale/services.py. This is UX only — Django is the
 * authoritative source of truth and re-validates at checkout.
 */
export const getWholesaleOrderMinimum = (
    profile: ProfileResponse | null | undefined
): number => {
    if (profile?.has_completed_initial_wholesale_order) {
        return WHOLESALE_REORDER_MINIMUM;
    }

    return WHOLESALE_INITIAL_ORDER_MINIMUM;
};

const wholesaleItems = (cartItems: CartItem[]): CartItem[] =>
    cartItems.filter(
        (item) => item.wholesale_minimum_quantity !== undefined
    );

export const getWholesaleCartTotalQuantity = (
    cartItems: CartItem[]
): number =>
    wholesaleItems(cartItems).reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
    );

export const meetsSkuMinimums = (cartItems: CartItem[]): boolean =>
    wholesaleItems(cartItems).every(
        (item) =>
            Number(item.quantity || 0) >=
            Number(item.wholesale_minimum_quantity || 0)
    );

export const meetsWholesaleOrderMinimum = (
    cartItems: CartItem[],
    profile: ProfileResponse | null | undefined
): boolean =>
    getWholesaleCartTotalQuantity(cartItems) >=
    getWholesaleOrderMinimum(profile);

export const getWholesaleCheckoutBlockers = (
    cartItems: CartItem[],
    profile: ProfileResponse | null | undefined
): string[] => {
    const blockers: string[] = [];

    wholesaleItems(cartItems).forEach((item) => {
        const minimum = Number(item.wholesale_minimum_quantity || 0);
        const quantity = Number(item.quantity || 0);

        if (quantity < minimum) {
            blockers.push(
                `The minimum wholesale quantity for ${item.name} is ${minimum} units.`
            );
        }
    });

    const orderMinimum = getWholesaleOrderMinimum(profile);
    const totalQuantity = getWholesaleCartTotalQuantity(cartItems);

    if (totalQuantity > 0 && totalQuantity < orderMinimum) {
        const remaining = orderMinimum - totalQuantity;
        const orderType = profile?.has_completed_initial_wholesale_order
            ? "reorders"
            : "opening orders";

        blockers.push(
            `Wholesale ${orderType} require a minimum of ${orderMinimum} units. ` +
                `Add ${remaining} more unit${remaining === 1 ? "" : "s"} to continue to checkout.`
        );
    }

    return blockers;
};
